import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  InvitationPage,
  LoginPage,
  RecoveryPage,
  ResetPasswordPage,
} from "./auth/AuthPages";
import { useAuth } from "./auth/AuthProvider";
import { AppShell } from "./components/AppShell";
import { StatePanel } from "./components/ui";
import { HomePage } from "./features/workspace/HomePage";
import { caseUrl, funnelUrl } from "./features/workspace/shared";
import { CasePage } from "./features/workspace/CasePage";
import { FunnelPage } from "./features/workspace/FunnelPage";
import { DataProvider } from "./data/DataProvider";
import type { Permission, ServiceType } from "./data/types";

const OrdersPage = lazy(() =>
  import("./pages/OrdersPage").then((module) => ({
    default: module.OrdersPage,
  })),
);
const ReceivablesPage = lazy(() =>
  import("./features/collections/ReceivablesPage").then((module) => ({
    default: module.ReceivablesPage,
  })),
);
const AgendaPage = lazy(() =>
  import("./pages/AgendaPage").then((module) => ({
    default: module.AgendaPage,
  })),
);
const ConfirmationsPage = lazy(() =>
  import("./pages/ConfirmationsPage").then((module) => ({
    default: module.ConfirmationsPage,
  })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((module) => ({
    default: module.SettingsPage,
  })),
);
const WhatsAppPage = lazy(() =>
  import("./pages/WhatsAppPage").then((module) => ({
    default: module.WhatsAppPage,
  })),
);

function ProtectedApp() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading")
    return (
      <div className="fullscreen-state">
        <StatePanel state="loading" title="Validando sesión" />
      </div>
    );
  if (status !== "authenticated")
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: { pathname: location.pathname, search: location.search },
        }}
      />
    );
  return (
    <DataProvider>
      <AppShell />
    </DataProvider>
  );
}

function RequirePermission({
  any,
  children,
}: {
  any: Permission[];
  children: ReactNode;
}) {
  const { hasPermission } = useAuth();
  return any.some(hasPermission) ? children : <StatePanel state="forbidden" />;
}

function RouteLoader() {
  return (
    <StatePanel
      state="loading"
      title="Preparando sección"
      description="Cargando sólo lo necesario para esta pantalla."
    />
  );
}

function NotFoundPage() {
  return (
    <div className="not-found-page">
      <StatePanel
        state="empty"
        title="Esta sección no existe"
        description="Revisa la dirección o vuelve al resumen."
      />
      <a className="button button--primary" href="/">
        Volver al resumen
      </a>
    </div>
  );
}

function LegacyLeadsRedirect() {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const id = search.get("open");
  search.delete("open");
  const destination = funnelUrl("school");
  return (
    <Navigate
      to={id ? caseUrl("lead", id, destination) : destination}
      replace
    />
  );
}

/** /vuelos, /tours and /grupos-escolares used to be separate sections; they now open the funnel filtered by service. */
function LegacyServiceRedirect({ service }: { service: ServiceType }) {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const view =
    search.get("view") ??
    (search.has("state") || search.has("open") ? "bookings" : "followup");
  const openId = search.get("open");
  if (view === "agenda") {
    search.delete("view");
    search.set("service", service);
    return <Navigate to={`/agenda?${search}`} replace />;
  }
  const keep: Record<string, string> = {};
  for (const key of ["q", "state", "page", "layout", "scope", "stage"]) {
    const value = search.get(key);
    if (value) keep[key] = value;
  }
  const destination = funnelUrl(
    service,
    view === "bookings" ? "reservas" : "ventas",
    keep,
  );
  if (openId)
    return (
      <Navigate
        to={caseUrl(
          service === "school" && view !== "bookings" ? "lead" : "booking",
          openId,
          destination,
        )}
        replace
      />
    );
  return <Navigate to={destination} replace />;
}

function LegacySettingsRedirect({
  section,
}: {
  section: "team" | "audit" | "system";
}) {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  search.set("section", section);
  return <Navigate to={`/ajustes?${search.toString()}`} replace />;
}

export default function App() {
  return (
    <>
      <nav className="skip-navigation" aria-label="Acceso rápido">
        <a className="skip-link" href="#main-content">
          Ir al contenido principal
        </a>
      </nav>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/mfa/*" element={<Navigate to="/login" replace />} />
        <Route path="/recuperar" element={<RecoveryPage />} />
        <Route path="/restablecer" element={<ResetPasswordPage />} />
        <Route path="/invitacion" element={<InvitationPage />} />
        <Route element={<ProtectedApp />}>
          <Route
            index
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequirePermission any={["dashboard.read", "crm.read"]}>
                  <HomePage />
                </RequirePermission>
              </Suspense>
            }
          />
          <Route
            path="casos/:kind/:id"
            element={
              <RequirePermission
                any={[
                  "crm.read",
                  "bookings.read",
                  "orders.read",
                  "leads.followup",
                ]}
              >
                <CasePage />
              </RequirePermission>
            }
          />
          <Route
            path="embudo"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequirePermission
                  any={["crm.read", "bookings.read", "bookings.read_assigned"]}
                >
                  <FunnelPage />
                </RequirePermission>
              </Suspense>
            }
          />
          <Route
            path="mensajes"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequirePermission any={["whatsapp.read"]}>
                  <WhatsAppPage />
                </RequirePermission>
              </Suspense>
            }
          />
          <Route
            path="bandeja"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequirePermission any={["whatsapp.read"]}>
                  <WhatsAppPage />
                </RequirePermission>
              </Suspense>
            }
          />
          <Route path="oportunidades" element={<Navigate to="/embudo" replace />} />
          <Route path="clientes" element={<Navigate to="/" replace />} />
          <Route path="reportes" element={<Navigate to="/" replace />} />
          <Route path="indicadores" element={<Navigate to="/" replace />} />
          <Route
            path="pedidos"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequirePermission any={["orders.read"]}>
                  <OrdersPage />
                </RequirePermission>
              </Suspense>
            }
          />
          <Route path="vuelos" element={<LegacyServiceRedirect service="balloon" />} />
          <Route path="tours" element={<LegacyServiceRedirect service="tour" />} />
          <Route path="grupos-escolares" element={<LegacyServiceRedirect service="school" />} />
          <Route
            path="agenda"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequirePermission
                  any={["bookings.read", "bookings.read_assigned"]}
                >
                  <AgendaPage />
                </RequirePermission>
              </Suspense>
            }
          />
          <Route
            path="por-cobrar"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequirePermission any={["finance.read"]}>
                  <ReceivablesPage />
                </RequirePermission>
              </Suspense>
            }
          />
          <Route path="reservas" element={<Navigate to="/embudo?vista=reservas" replace />} />
          <Route
            path="confirmaciones"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequirePermission any={["confirmations.read"]}>
                  <ConfirmationsPage />
                </RequirePermission>
              </Suspense>
            }
          />
          <Route path="pendientes" element={<Navigate to="/" replace />} />
          <Route
            path="leads"
            element={
              <RequirePermission any={["leads.followup"]}>
                <LegacyLeadsRedirect />
              </RequirePermission>
            }
          />
          <Route
            path="equipo"
            element={
              <RequirePermission any={["team.read"]}>
                <LegacySettingsRedirect section="team" />
              </RequirePermission>
            }
          />
          <Route
            path="auditoria"
            element={
              <RequirePermission any={["audit.read"]}>
                <LegacySettingsRedirect section="audit" />
              </RequirePermission>
            }
          />
          <Route
            path="sistema"
            element={
              <RequirePermission any={["system.read"]}>
                <LegacySettingsRedirect section="system" />
              </RequirePermission>
            }
          />
          <Route
            path="ajustes"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequirePermission
                  any={[
                    "settings.manage",
                    "team.read",
                    "audit.read",
                    "system.read",
                  ]}
                >
                  <SettingsPage />
                </RequirePermission>
              </Suspense>
            }
          />
          <Route
            path="whatsapp"
            element={
              <Suspense fallback={<RouteLoader />}>
                <RequirePermission any={["whatsapp.read"]}>
                  <WhatsAppPage />
                </RequirePermission>
              </Suspense>
            }
          />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
